/**
 * ============================================================================
 * useMembershipsData - MEMBERSHIP PACKAGES DATA FETCHING HOOK
 * ============================================================================
 *
 * Production-ready TanStack Query hook for membership packages
 *
 * ============================================================================
 */

import { useQuery } from '@tanstack/react-query';
import { vendorApi } from '../services/api';
import type { MembershipPackage, MembershipStats, MembershipFilters } from '../types/memberships';

interface MembershipsDataResponse {
  packages: MembershipPackage[];
  stats: MembershipStats;
  totalCount: number;
}

/**
 * Fetches membership packages with stats
 */
export function useMembershipsData(
  venueSlug: string | undefined,
  filters: MembershipFilters
) {
  return useQuery<MembershipsDataResponse, Error>({
    queryKey: ['memberships', venueSlug, filters.status, filters.search],

    queryFn: async () => {
      if (!venueSlug) throw new Error('Venue slug is required');

      // Fetch membership packages from database (not JSON field)
      const response = await vendorApi.getMembershipPackages(venueSlug);

      const backendPackages = response.data?.packages || [];

      // Transform backend data to frontend MembershipPackage interface
      const packages: MembershipPackage[] = backendPackages.map((pkg: any) => {
        // Parse privileges/facilities
        const facilities = pkg.privileges_and_access
          ? pkg.privileges_and_access.split('\n').filter((f: string) => f.trim())
          : [];

        return {
          id: pkg.id, // Real database ID
          name: pkg.package_name || '',
          description: pkg.description || '',
          price: parseFloat(pkg.price) || 0,
          duration: pkg.duration || pkg.duration_months || 1,
          duration_type: (pkg.duration_type || 'month') as MembershipPackage['duration_type'],
          facilities,
          status: pkg.is_active ? 'active' : 'inactive',
          venue_id: pkg.venue_id || 0,
          created_at: pkg.created_at || new Date().toISOString(),
          updated_at: pkg.updated_at || new Date().toISOString(),
          members_count: pkg.active_memberships_count || 0, // Real member count from database
          total_revenue: parseFloat(pkg.total_revenue) || 0, // Real revenue from database
        };
      });

      // Apply client-side filtering
      let filteredPackages = packages;

      // Status filter
      if (filters.status !== 'all') {
        filteredPackages = filteredPackages.filter(
          (pkg: MembershipPackage) => pkg.status === filters.status
        );
      }

      // Search filter
      if (filters.search.trim()) {
        const query = filters.search.toLowerCase();
        filteredPackages = filteredPackages.filter(
          (pkg: MembershipPackage) =>
            pkg.name.toLowerCase().includes(query) ||
            pkg.description.toLowerCase().includes(query)
        );
      }

      // Use backend stats if available, otherwise calculate from filtered packages
      const backendStats = response.data?.stats;

      const stats: MembershipStats = backendStats
        ? {
            totalPackages: backendStats.total_packages || 0,
            activePackages: backendStats.active_packages || 0,
            totalMembers: backendStats.total_memberships_sold || 0,
            monthlyRevenue: parseFloat(backendStats.total_revenue) || 0,
          }
        : calculateStats(packages);

      return {
        packages: filteredPackages,
        stats,
        totalCount: filteredPackages.length,
      };
    },

    enabled: !!venueSlug,
    staleTime: 5 * 60 * 1000, // 5 minutes - packages don't change frequently
    gcTime: 15 * 60 * 1000, // 15 minutes in cache
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

/**
 * Calculate stats from packages list
 */
function calculateStats(packages: MembershipPackage[]): MembershipStats {
  const activePackages = packages.filter((pkg) => pkg.status === 'active').length;

  const totalMembers = packages.reduce(
    (sum, pkg) => sum + (pkg.members_count || 0),
    0
  );

  const monthlyRevenue = packages.reduce(
    (sum, pkg) => sum + (pkg.total_revenue || 0),
    0
  );

  return {
    totalPackages: packages.length,
    activePackages,
    totalMembers,
    monthlyRevenue,
  };
}
